import {
  AllCommunityModule,
  colorSchemeDark,
  ModuleRegistry,
  themeBalham,
  ColDef,
  GetRowIdParams,
  GridApi,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { FinxaiTrade } from "../models/finxaiTrade";
import { OrderStatus } from "../models/protobuff-objects/finxai_web_pb";
import { RootState } from "../store/store";
import {
  getOrderStatusString,
  filterOrdersByType,
  OrderFilterType,
} from "../utils/orderUtils";
import QuantityCell from "./quantity-cell";

ModuleRegistry.registerModules([AllCommunityModule]);

// Fallback data when tradeOrders is empty - moved outside component
const fallbackData: FinxaiTrade[] = [
  //{ order_id: "1", parent_ord_id: "1", security_id: "1", ticker_symbol: "AAPL", side: "BUY", orderStatus: OrderStatus.ORD_STATUS_NEW, bid_px: 150.25, offer_px: 150.30, price: 150.27, order_qty: 100, account: "ACC001", avg_fill_px: 150.27, filled_qty: 100 },
  //{ order_id: "2", parent_ord_id: "2", security_id: "2", ticker_symbol: "MSFT", side: "SELL", orderStatus: OrderStatus.ORD_STATUS_FILLED, bid_px: 300.15, offer_px: 300.20, price: 300.18, order_qty: 50, account: "ACC002", avg_fill_px: 300.18, filled_qty: 50 },
  //{ order_id: "3", parent_ord_id: "3", security_id: "3", ticker_symbol: "GOOGL", side: "BUY", orderStatus: OrderStatus.ORD_STATUS_PARTIALLY_FILLED, bid_px: 2500.00, offer_px: 2500.50, price: 2500.25, order_qty: 10, account: "ACC001", avg_fill_px: 2500.25, filled_qty: 10 },
];

// Helper function to format numbers with fallback to '-'
const numberFormatter = (params: any) => {
  const value = params.value;
  if (
    value === null ||
    value === undefined ||
    isNaN(value) ||
    !isFinite(value)
  ) {
    return "-";
  }
  return value;
};

// Column Definitions - moved outside component to prevent recreation
const colDefs: ColDef<FinxaiTrade>[] = [
  { field: "ticker_symbol" as const, headerName: "Instrument", width: 250 },
  {
    field: "side" as const,
    headerName: "Side",
    cellClassRules: {
      "buy-green": (params) => params.value === "BUY",
      "sell-red": (params) => params.value === "SELL",
    },
    width: 100,
  },
  {
    field: "orderStatus" as const,
    headerName: "Order Status",
    cellRenderer: (params: any) => getOrderStatusString(params.data),
    comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
      const statusA = getOrderStatusString(nodeA.data);
      const statusB = getOrderStatusString(nodeB.data);
      return statusA.localeCompare(statusB);
    },
  },
  //{ field: "bid_px" as const , headerName: "Bid Price", cellDataType: 'number', cellStyle: { textAlign: 'right' }, valueFormatter: numberFormatter },
  //{ field: "offer_px" as const , headerName: "Offer Price", cellDataType: 'number', cellStyle: { textAlign: 'right' }, valueFormatter: numberFormatter },
  {
    field: "price" as const,
    headerName: "Order Price",
    cellDataType: "number",
    cellStyle: { textAlign: "right" },
    valueFormatter: numberFormatter,
  },
  {
    headerName: "Filled/Size",
    valueGetter: (params: any) => {
      // Return a combined value that changes when either field changes
      // This ensures AG Grid tracks changes to both filled_qty and order_qty
      return `${params.data?.filled_qty ?? 0}|${params.data?.order_qty ?? 0}`;
    },
    cellRenderer: (params: any) => {
      const filledQty = params.data?.filled_qty ?? 0;
      const orderQty = params.data?.order_qty ?? 0;
      return <QuantityCell quantity={orderQty} filledQuantity={filledQty} />;
    },
    comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
      // Sort based on filled percentage (filled_qty / order_qty)
      const filledA = nodeA.data?.filled_qty ?? 0;
      const orderA = nodeA.data?.order_qty ?? 0;
      const filledB = nodeB.data?.filled_qty ?? 0;
      const orderB = nodeB.data?.order_qty ?? 0;

      // Calculate percentage, handling division by zero
      const percentageA = orderA > 0 ? filledA / orderA : 0;
      const percentageB = orderB > 0 ? filledB / orderB : 0;

      return percentageA - percentageB;
    },
  },
  { field: "account" as const, headerName: "Account" },
  {
    headerName: "Allocated",
    valueGetter: (params: any) => {
      return params.data?.is_allocated ?? false;
    },
    width: 120,
    cellRenderer: (params: any) => {
      const isAllocated = params.data?.is_allocated ?? false;
      return (
        <input
          type="checkbox"
          checked={isAllocated}
          disabled={true}
          style={{ cursor: "not-allowed" }}
        />
      );
    },
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },
  //{ field: "orderStatus" as const , headerName: "Order Status"},
];

interface GridExampleProps {
  orderType?: OrderFilterType;
}

const GridExample = React.memo(({ orderType = "All" }: GridExampleProps) => {
  // Grid API reference
  const gridApiRef = useRef<GridApi<FinxaiTrade> | null>(null);

  // Get trade orders from Redux store - use shallowEqual to prevent unnecessary re-renders
  const tradeOrders = useSelector(
    (state: RootState) => state.data.tradeOrders,
    shallowEqual
  );

  // Memoize row data to prevent unnecessary recalculations
  const effectiveRowData = useMemo(() => {
    const data =
      tradeOrders && tradeOrders.length > 0 ? tradeOrders : fallbackData;
    const filtered = data;
    return filterOrdersByType(filtered, orderType);
    //return data;
  }, [tradeOrders, orderType]);

  // Provide unique row ID for AG Grid to track rows efficiently
  const getRowId = useMemo(
    () => (params: GetRowIdParams<FinxaiTrade>) => params.data.order_id,
    []
  );

  // Handle grid ready - size columns properly
  const onGridReady = useCallback((params: any) => {
    gridApiRef.current = params.api;
    // Auto-size columns based on content
    params.api.autoSizeAllColumns();
  }, []);

  // Check if an order is filled
  const isFilledOrder = useCallback((params: any) => {
    const order = params.data as FinxaiTrade;
    return order.filled_qty === order.order_qty && order.filled_qty > 0;
  }, []);

  return (
    // Data Grid will fill the size of the parent container
    <div style={{ height: "100%", width: "100%" }}>
      <style>
        {`
                    .buy-green {
                        color: #22c55e !important;
                        font-weight: bold;
                    }
                    .sell-red {
                        color: #ef4444 !important;
                        font-weight: bold;
                    }
                    .filled-order {
                        opacity: 0.75;
                        background-color: rgba(34, 197, 94, 0.08) !important;
                    }
                    .filled-order:hover {
                        opacity: 0.85;
                    }
                `}
      </style>
      <AgGridReact
        autoSizeStrategy={{ type: "fitProvidedWidth", width: 850 }}
        theme={themeBalham.withPart(colorSchemeDark)}
        rowData={effectiveRowData}
        columnDefs={colDefs}
        getRowId={getRowId}
        onGridReady={onGridReady}
        animateRows={true}
        rowClassRules={{
          "filled-order": isFilledOrder,
        }}
      />
    </div>
  );
});

GridExample.displayName = "GridExample";

export default GridExample;
